import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { videoUrl } from "../lib/api";

/**
 * Reusable cinematic food video band.
 * Props:
 *  - videoSrc: path under /api/static/videos/
 *  - posterSrc: path under /api/static/videos/
 *  - eyebrow: small uppercase label ("03 — Appétit")
 *  - title: big display headline (JSX allowed)
 *  - tagline: optional small subtitle text
 *  - height: tailwind height class (default "h-[70vh]")
 *  - testId: data-testid prefix
 */
export const VideoBand = ({
  videoSrc,
  posterSrc,
  eyebrow,
  title,
  tagline,
  height = "h-[70vh]",
  testId = "video-band",
}) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Section needs to be near the viewport before we even start fetching the
  // video bytes. Until then, the poster background carries the visual.
  const [shouldLoad, setShouldLoad] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.12]);

  const VIDEO = videoUrl(videoSrc);
  const POSTER = videoUrl(posterSrc);

  useEffect(() => {
    const section = ref.current;
    if (!section) return;
    // Stage 1: load gate — start fetching once we're within ~400px of the section.
    const loadObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          loadObs.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    loadObs.observe(section);
    return () => loadObs.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    const video = videoRef.current;
    const section = ref.current;
    if (!video || !section) return;

    if (video.readyState >= 2) setVideoReady(true);
    const onReady = () => setVideoReady(true);
    const onError = () => { setFailed(true); setVideoReady(false); };
    video.addEventListener("canplay", onReady);
    video.addEventListener("loadeddata", onReady);
    video.addEventListener("error", onError);

    // Stage 2: play gate — once the section is properly in view, play; pause when out.
    const playObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.01, rootMargin: "200px 0px" }
    );
    playObs.observe(section);

    // iOS autoplay fallback: retry on first user gesture if blocked.
    const retry = () => { if (video.paused) video.play().catch(() => {}); };
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", retry, opts);
    window.addEventListener("scroll", retry, opts);

    return () => {
      playObs.disconnect();
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("error", onError);
      window.removeEventListener("touchstart", retry);
      window.removeEventListener("scroll", retry);
    };
  }, [shouldLoad]);

  return (
    <section
      ref={ref}
      data-testid={testId}
      className={`relative ${height} min-h-[500px] w-full overflow-hidden bg-black`}
      style={{
        backgroundImage: `url(${POSTER})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        {/* Poster XOR video: poster carries the visual while the video buffers, then fades out
            cleanly so the two layers never compose into a ghosted double image. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${POSTER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: videoReady && !failed ? 0 : 1,
            transition: "opacity 0.6s ease-out",
          }}
        />
        {shouldLoad && (
          <video
            ref={videoRef}
            src={VIDEO}
            poster={POSTER}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`${testId}-video`}
            style={{
              opacity: videoReady && !failed ? 1 : 0,
              transition: "opacity 0.6s ease-out",
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/20"></div>
      </motion.div>

      <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 lg:px-14 pb-14 lg:pb-20">
        {eyebrow && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="tag-label mb-5 flex items-center gap-3"
          >
            <span className="w-10 h-px bg-[#FFC300]"></span>
            <span>{eyebrow}</span>
          </motion.div>
        )}
        {title && (
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase tracking-tighter leading-[0.9] max-w-4xl"
            data-testid={`${testId}-title`}
          >
            {title}
          </motion.h2>
        )}
        {tagline && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 max-w-xl font-body text-base md:text-lg text-zinc-200"
          >
            {tagline}
          </motion.p>
        )}
      </div>
    </section>
  );
};
